<template>
  <RevDrawer
    :name="MODAL_NAME"
    size="large"
    :title="i18n(translations.listingDetail)"
    @close="handleClose"
  >
    <template #body>
      <RevLoadingScreen
        v-if="isLoadingListingInfo"
        :text="i18n(translations.loadingText)"
      />

      <template v-else-if="!isEmpty(listingInfo)">
        <ListingInfo v-if="listingInfo" :listing="listingInfo" />

        <RevTabs
          class="my-24"
          :label="i18n(translations.tabsBarLabel)"
          scrollable
        >
          <RevTabItem
            v-for="tab in tabs"
            :key="tab.tabName"
            :active="listingTab === tab.tabName"
            :label="i18n(tab.translationKey)"
            @click="handleChangeTab(tab.tabName)"
          />
        </RevTabs>

        <component :is="activeTabComponent" :listing="listingInfo" />
      </template>

      <template v-else>
        {{ i18n(translations.listingNotFound) }}
      </template>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { navigateTo, onBeforeRouteLeave, useRoute } from '#imports'
import { computed, ref, watch } from 'vue'

import { HttpApiError } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevDrawer } from '@ds/components/Drawer'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { closeModal, openModal } from '@ds/components/ModalBase'
import { RevTabItem } from '@ds/components/TabItem'
import { RevTabs } from '@ds/components/Tabs'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import {
  MODAL_NAME,
  QUERY_MODAL_NAME,
  QUERY_MODAL_TAB_NAME,
  TABS_INFO,
} from './ListingDetailsDrawer.constants'
import translations from './ListingDetailsDrawer.translations'
import ListingInfo from './components/ListingInfo/ListingInfo.vue'

const { openErrorToast } = useTheToast()

const { hasAccessToNewShippingPage } = useSellerInfoStore()
const i18n = useI18n()
const route = useRoute()
const isLoadingListingInfo = ref(false)
const listingId = ref<string | undefined>(undefined)
const listingTab = ref<string>(TABS_INFO[0].tabName)
const listingInfo = ref()
const alreadyLeavingRoute = ref(false)
const $httpFetch = useHttpFetch()

async function getListingInfo() {
  try {
    isLoadingListingInfo.value = true

    const data = await $httpFetch(
      '/api/seller-experience/listings/:listingId',
      {
        pathParams: {
          listingId: listingId.value || '',
        },
      },
    )

    listingInfo.value = data
  } catch (error) {
    openErrorToast({
      content: HttpApiError.fromAnyError(error as Error)?.message,
    })
    closeModal(MODAL_NAME)
  } finally {
    isLoadingListingInfo.value = false
  }
}

const activeTabComponent = computed(() => {
  return (
    TABS_INFO.find((tab) => tab.tabName === listingTab.value)?.component ||
    TABS_INFO[0].component
  )
})

const tabs = computed(() => {
  return TABS_INFO.filter((tab) => {
    return tab.tabName === 'shipping' ? !hasAccessToNewShippingPage : true
  })
})

watch(
  () => route.hash,
  async (newHash, oldHash) => {
    if (newHash && newHash !== oldHash) {
      const hashParams = route.hash.split('#')
      const hashListingId = hashParams.find((param) =>
        param.includes(QUERY_MODAL_NAME),
      )
      const hashListingTab = hashParams.find((param) =>
        param.includes(QUERY_MODAL_TAB_NAME),
      )

      listingId.value = hashListingId?.split('=')[1]
      listingTab.value = hashListingTab?.split('=')[1] || ''

      if (listingTab.value && listingId.value) {
        getListingInfo()
        openModal(MODAL_NAME)
      }
    }
  },
  { immediate: true },
)

async function handleClose() {
  listingId.value = ''
  listingTab.value = TABS_INFO[0].tabName

  if (alreadyLeavingRoute.value) {
    return
  }

  await navigateTo({
    ...route,
    query: route.query,
    hash: '',
    replace: true,
  })
}

async function handleChangeTab(tab: string) {
  await navigateTo({
    ...route,
    query: route.query,
    hash: `#${QUERY_MODAL_NAME}=${listingId.value}#${QUERY_MODAL_TAB_NAME}=${tab}`,
    replace: true,
  })
}

// Whenever navigation to a different route (such as to the duplicate listing page) takes place, the modal is closed.
// Closing the modal unfortunately triggers a second navigation, which clashes with the first navigation to a different route.
// To prevent this double navigation we set a temporary value to indicate the first (correct) navigation is already in place,
// we can then based on this value ignore the second navigation call.
onBeforeRouteLeave(() => {
  alreadyLeavingRoute.value = true
})
</script>
